import React, { useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import cs from 'classnames';

import s from './AnimatedBackground.module.scss';
import { Grid, getGrid } from './getGrid';
import { Lines } from '../Line/Lines';
import { Clouds } from '../Clouds/Clouds';
import debounce from 'lodash/debounce';
import { getImpulseFromRightDuration } from '../Line/impulseFromRightConfig';
import { getImpulseFromCenterDuration } from '../Line/impulseFromCenterConfig';

const GAP_HORIZONTAL = 49;
const GAP_VERTICAL = 81;

export interface AnimatedBackgroundRef {
  impulseFromRight: () => Promise<void>;
  impulseFromCenter: () => Promise<void>;
}

interface AnimatedBackgroundProps {
  theme: 'light' | 'dark';
  isIdle: boolean;
  onReady?: () => void;
}

export const AnimatedBackground = forwardRef<
  AnimatedBackgroundRef,
  AnimatedBackgroundProps
>(({ theme, isIdle, onReady }, ref) => {
  useImperativeHandle(
    ref,
    () => {
      return {
        impulseFromRight: () => {
          return new Promise((resolve) => {
            containerRef.current?.classList.add('ANIMATION_IMPULSE_FROM_RIGHT');

            const duration = getImpulseFromRightDuration(
              grid.horizontalPositions.length,
            );
            setTimeout(() => {
              containerRef.current?.classList.remove(
                'ANIMATION_IMPULSE_FROM_RIGHT',
              );
              resolve();
            }, duration * 1000);
          });
        },
        impulseFromCenter: () => {
          return new Promise((resolve) => {
            containerRef.current?.classList.add(
              'ANIMATION_IMPULSE_FROM_CENTER',
            );

            const duration = getImpulseFromCenterDuration(
              grid.horizontalPositions.length,
              grid.verticalPositions.length,
            );
            setTimeout(() => {
              containerRef.current?.classList.remove(
                'ANIMATION_IMPULSE_FROM_CENTER',
              );
              resolve();
            }, duration * 1000);

            resolve();
          });
        },
      };
    },
    [],
  );

  const containerRef = React.useRef<HTMLDivElement>(null);

  const [containerSize, setContainerSize] = React.useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });
  const [grid, setGrid] = React.useState<Grid>({
    verticalPositions: [],
    horizontalPositions: [],
  });

  useLayoutEffect(() => {
    let onReadyFired = false;
    const containerElement = containerRef.current;

    const onResize = debounce((entries) => {
      const containerWidth = entries[0].contentRect.width;
      const containerHeight = entries[0].contentRect.height;

      setGrid(
        getGrid({
          width: containerWidth,
          height: containerHeight,
          gapHorizontal: GAP_HORIZONTAL,
          gapVertical: GAP_VERTICAL,
        }),
      );

      setContainerSize({
        width: containerWidth,
        height: containerHeight,
      });

      if (!onReadyFired) {
        setTimeout(() => {
          onReady?.();
          onReadyFired = true;
        }, 50);
      }
    }, 300);

    const resizeObserver = new ResizeObserver(onResize);

    if (containerElement) {
      resizeObserver.observe(containerElement);
    }

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
    };
  }, []);

  const classNames = cs(s.container, {
    [s.containerLight]: theme === 'light',
    [s.containerDark]: theme === 'dark',
  });
  const containerStyle: Record<string, string> = {
    '--container-width': `${containerSize.width}px`,
    '--container-height': `${containerSize.height}px`,
  };

  return (
    <div className={classNames} style={containerStyle} ref={containerRef}>
      <Clouds theme={theme} />

      <Lines
        positions={grid.horizontalPositions}
        orientation="horizontal"
        isIdle={isIdle}
      />
      <Lines
        positions={grid.verticalPositions}
        orientation="vertical"
        isIdle={isIdle}
      />
    </div>
  );
});
